import type { ReactElement } from 'react';

export type TAuthGraphicVariant =
  | 'login'
  | 'register'
  | 'magic-link'
  | 'two-factor'
  | 'forgot-password'
  | 'change-password'
  | 'validate-account'
  | 'signature-link'
  | 'organization-invite'
  | 'admin-promotion'
  | 'logout';

interface IAuthGraphicProps {
  variant: TAuthGraphicVariant;
  label: string;
}

function VariantScene({ variant }: { variant: TAuthGraphicVariant }): ReactElement {
  if (variant === 'magic-link') {
    return (
      <>
        <path d="M206 202h204v124H206z" fill="var(--auth-graphic-surface)" stroke="var(--auth-graphic-line)" strokeWidth="3" />
        <path d="m209 205 99 74 99-74" fill="none" stroke="var(--auth-graphic-accent)" strokeWidth="4" />
        <path d="M258 322c22-31 74-31 100 0" fill="none" stroke="var(--auth-graphic-warm)" strokeWidth="5" strokeLinecap="round" />
        <path d="M184 170h66M366 356h66" stroke="var(--auth-graphic-muted)" strokeWidth="3" strokeLinecap="round" />
      </>
    );
  }

  if (variant === 'two-factor') {
    return (
      <>
        <rect
          x="246"
          y="144"
          width="128"
          height="218"
          rx="28"
          fill="var(--auth-graphic-surface)"
          stroke="var(--auth-graphic-line)"
          strokeWidth="3"
        />
        <rect x="267" y="186" width="86" height="52" rx="12" fill="var(--auth-graphic-soft)" />
        <path d="M283 276h54M283 302h54" stroke="var(--auth-graphic-muted)" strokeWidth="4" strokeLinecap="round" />
        <path d="M287 210h45" stroke="var(--auth-graphic-accent)" strokeWidth="6" strokeLinecap="round" />
        <circle cx="310" cy="340" r="8" fill="var(--auth-graphic-warm)" />
        <path d="M193 232h38M389 232h38M193 284h38M389 284h38" stroke="var(--auth-graphic-line)" strokeWidth="3" strokeLinecap="round" />
      </>
    );
  }

  if (variant === 'register') {
    return (
      <>
        <rect
          x="210"
          y="154"
          width="196"
          height="186"
          rx="28"
          fill="var(--auth-graphic-surface)"
          stroke="var(--auth-graphic-line)"
          strokeWidth="3"
        />
        <circle cx="308" cy="210" r="36" fill="var(--auth-graphic-soft)" stroke="var(--auth-graphic-accent)" strokeWidth="5" />
        <path d="M248 298c18-33 102-33 120 0" fill="none" stroke="var(--auth-graphic-accent)" strokeWidth="6" strokeLinecap="round" />
        <path
          d="M390 151c24 10 44 30 56 57M171 288c11 27 35 48 65 57"
          stroke="var(--auth-graphic-muted)"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <path d="M423 278v58M394 307h58" stroke="var(--auth-graphic-warm)" strokeWidth="7" strokeLinecap="round" />
      </>
    );
  }

  if (variant === 'forgot-password') {
    return (
      <>
        <rect
          x="208"
          y="184"
          width="200"
          height="136"
          rx="22"
          fill="var(--auth-graphic-surface)"
          stroke="var(--auth-graphic-line)"
          strokeWidth="3"
        />
        <path
          d="M246 184v-24c0-35 25-58 62-58s62 23 62 58v24"
          fill="none"
          stroke="var(--auth-graphic-muted)"
          strokeWidth="14"
          strokeLinecap="round"
        />
        <circle cx="308" cy="244" r="24" fill="var(--auth-graphic-soft)" stroke="var(--auth-graphic-accent)" strokeWidth="4" />
        <path d="M308 268v26" stroke="var(--auth-graphic-accent)" strokeWidth="6" strokeLinecap="round" />
        <path d="M412 334c-23 31-76 37-111 11" fill="none" stroke="var(--auth-graphic-warm)" strokeWidth="6" strokeLinecap="round" />
        <path
          d="M401 315 414 336l-25 5"
          fill="none"
          stroke="var(--auth-graphic-warm)"
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    );
  }

  if (variant === 'change-password') {
    return (
      <>
        <rect
          x="214"
          y="176"
          width="188"
          height="152"
          rx="24"
          fill="var(--auth-graphic-surface)"
          stroke="var(--auth-graphic-line)"
          strokeWidth="3"
        />
        <path
          d="M258 176v-24c0-29 21-49 50-49s50 20 50 49v24"
          fill="none"
          stroke="var(--auth-graphic-muted)"
          strokeWidth="12"
          strokeLinecap="round"
        />
        <path d="M262 244h92M262 274h58" stroke="var(--auth-graphic-muted)" strokeWidth="5" strokeLinecap="round" />
        <path
          d="M389 347c35-17 57-51 57-95v-39l-57-22-57 22v39c0 44 22 78 57 95z"
          fill="var(--auth-graphic-soft)"
          stroke="var(--auth-graphic-warm)"
          strokeWidth="5"
        />
        <path d="M368 258h42M389 237v42" stroke="var(--auth-graphic-warm)" strokeWidth="5" strokeLinecap="round" />
      </>
    );
  }

  if (variant === 'validate-account') {
    return (
      <>
        <rect
          x="198"
          y="160"
          width="220"
          height="158"
          rx="24"
          fill="var(--auth-graphic-surface)"
          stroke="var(--auth-graphic-line)"
          strokeWidth="3"
        />
        <path d="m201 163 107 82 107-82" fill="none" stroke="var(--auth-graphic-accent)" strokeWidth="4" />
        <path
          d="m258 285 28 28 74-82"
          fill="none"
          stroke="var(--auth-graphic-warm)"
          strokeWidth="8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <rect x="210" y="348" width="196" height="34" rx="17" fill="var(--auth-graphic-soft)" />
        <path d="M238 365h16M282 365h16M326 365h16M370 365h16" stroke="var(--auth-graphic-muted)" strokeWidth="5" strokeLinecap="round" />
      </>
    );
  }

  if (variant === 'signature-link') {
    return (
      <>
        <path d="M232 122h124l54 54v202H232z" fill="var(--auth-graphic-surface)" stroke="var(--auth-graphic-line)" strokeWidth="3" />
        <path d="M356 122v56h54" fill="var(--auth-graphic-soft)" stroke="var(--auth-graphic-line)" strokeWidth="3" />
        <path d="M266 238h110M266 266h86" stroke="var(--auth-graphic-muted)" strokeWidth="5" strokeLinecap="round" />
        <path
          d="M266 322c31-30 54 24 88-10 13-13 24-17 42-12"
          fill="none"
          stroke="var(--auth-graphic-accent)"
          strokeWidth="5"
          strokeLinecap="round"
        />
        <path
          d="M199 207 177 185M177 207l22-22M434 344l22 22M456 344l-22 22"
          stroke="var(--auth-graphic-warm)"
          strokeWidth="5"
          strokeLinecap="round"
        />
      </>
    );
  }

  if (variant === 'organization-invite') {
    return (
      <>
        <rect
          x="220"
          y="152"
          width="176"
          height="176"
          rx="28"
          fill="var(--auth-graphic-surface)"
          stroke="var(--auth-graphic-line)"
          strokeWidth="3"
        />
        <path
          d="M264 304v-78h34v78M318 304v-116h34v116"
          fill="var(--auth-graphic-soft)"
          stroke="var(--auth-graphic-accent)"
          strokeWidth="4"
        />
        <circle cx="308" cy="118" r="34" fill="var(--auth-graphic-soft)" stroke="var(--auth-graphic-warm)" strokeWidth="5" />
        <path d="M308 98v40M288 118h40" stroke="var(--auth-graphic-warm)" strokeWidth="5" strokeLinecap="round" />
        <path d="M178 368h260" stroke="var(--auth-graphic-muted)" strokeWidth="4" strokeLinecap="round" />
        <circle cx="208" cy="368" r="10" fill="var(--auth-graphic-accent)" />
        <circle cx="408" cy="368" r="10" fill="var(--auth-graphic-accent)" />
      </>
    );
  }

  if (variant === 'admin-promotion') {
    return (
      <>
        <path
          d="M308 110c55 22 100 30 100 30v83c0 70-36 117-100 145-64-28-100-75-100-145v-83s45-8 100-30z"
          fill="var(--auth-graphic-surface)"
          stroke="var(--auth-graphic-line)"
          strokeWidth="3"
        />
        <path d="M308 158v154" stroke="var(--auth-graphic-muted)" strokeWidth="5" strokeLinecap="round" />
        <path d="M260 208h96M260 248h96M260 288h96" stroke="var(--auth-graphic-accent)" strokeWidth="5" strokeLinecap="round" />
        <path
          d="m379 174 14 15 31-35"
          fill="none"
          stroke="var(--auth-graphic-warm)"
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    );
  }

  if (variant === 'logout') {
    return (
      <>
        <rect
          x="218"
          y="160"
          width="180"
          height="160"
          rx="28"
          fill="var(--auth-graphic-surface)"
          stroke="var(--auth-graphic-line)"
          strokeWidth="3"
        />
        <path
          d="M278 242h122M371 212l34 30-34 30"
          fill="none"
          stroke="var(--auth-graphic-accent)"
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="248" cy="342" r="42" fill="var(--auth-graphic-soft)" stroke="var(--auth-graphic-warm)" strokeWidth="5" />
        <path
          d="m229 342 13 14 29-32"
          fill="none"
          stroke="var(--auth-graphic-warm)"
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    );
  }

  return (
    <>
      <path d="M204 146h208v168H204z" fill="var(--auth-graphic-surface)" stroke="var(--auth-graphic-line)" strokeWidth="3" />
      <path d="M204 146h208v42H204z" fill="var(--auth-graphic-soft)" stroke="var(--auth-graphic-line)" strokeWidth="3" />
      <path d="M236 224h126M236 254h88" stroke="var(--auth-graphic-muted)" strokeWidth="5" strokeLinecap="round" />
      <path
        d="M236 286c31-28 58 22 94-10 12-11 25-16 42-12"
        fill="none"
        stroke="var(--auth-graphic-accent)"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M402 334c31-14 50-45 50-84v-34l-50-20-50 20v34c0 39 19 70 50 84z"
        fill="var(--auth-graphic-soft)"
        stroke="var(--auth-graphic-warm)"
        strokeWidth="5"
      />
      <path
        d="m383 255 14 15 28-32"
        fill="none"
        stroke="var(--auth-graphic-warm)"
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  );
}

export function AuthGraphic({ variant, label }: IAuthGraphicProps): ReactElement {
  return (
    <figure className="auth-graphic" role="img" aria-label={label}>
      <svg className="auth-graphic-canvas" viewBox="0 0 616 442" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          className="auth-graphic-grid"
          d="M92 74h432M92 128h432M92 182h432M92 236h432M92 290h432M92 344h432M146 38v366M200 38v366M254 38v366M308 38v366M362 38v366M416 38v366M470 38v366"
        />
        <path
          d="M110 366c48-54 108-69 178-45 72 25 139 23 219-41"
          stroke="var(--auth-graphic-trace)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray="10 12"
        />
        <VariantScene variant={variant} />
      </svg>
    </figure>
  );
}
